<template>
  <UtdBanner v-if="isVisible"
             class="utd-message-banner"
             role="alert">
    <template #text>
      <p class="utd-message-banner-text">
        <span class="utd-message-banner-label">{{ bannerMessage }}</span>
        <button class="utd-message-banner-info-btn"
                aria-label="More information"
                @click="openInfoModal">
          <i class="wkce-icon-info" />
        </button>
      </p>
    </template>
  </UtdBanner>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import PubSub from '_acaSrc/utility/PubSub';
import { C_EVENTS } from '_acaSrc/utility/constants';
import UtdBanner from '_acaSrc/components/shared/utd/UtdBanner.vue';
import UtdCache from '_acaSrc/utility/UtdCache';
import IndexManager from '_acaSrc/utility/IndexManager';
import ServerMessaging from '_acaSrc/utility/ServerMessaging';

const BANNER_CACHE_KEY = 'utdMessageBannerData';

const store = useStore();
const isNotDesktopView = computed(() => store.getters['device/isNotDesktopView']);

const pubsub = new PubSub();
const cache = new UtdCache();
const isVisible = ref(false);
const bannerData = ref(null);

const bannerMessage = computed(() => {
    if (!bannerData.value) {
        return '';
    }
    return isNotDesktopView.value
        ? (bannerData.value.shortMessage ?? bannerData.value.message)
        : bannerData.value.message;
});

const updateBodyClass = (visible) => {
    new IndexManager().bodyCss
        .setOrClear('offNetworkBannerVisible', visible);
};

const showBanner = (data) => {
    if (data?.message) {
        bannerData.value = data;
        isVisible.value = true;
        cache.setPersistent(BANNER_CACHE_KEY, data);
        updateBodyClass(true);
    }
};

const hideBanner = () => {
    isVisible.value = false;
    bannerData.value = null;
    cache.removePersistent(BANNER_CACHE_KEY);
    updateBodyClass(false);
};

const isEmailVerified = computed(() => store.getters['profile/isEmailVerified']);

watch(isEmailVerified, (verified) => {
    if (verified) {
        hideBanner();
    }
});

const isLoggedIn = computed(() => store.getters['user/userLoggedIn']);
const isAppDataLoaded = computed(
    () => store.getters['app/isAppDataLoaded']
);

watch(isLoggedIn, (loggedIn) => {
    if (!loggedIn) {
        hideBanner();
    }
});

watch(isAppDataLoaded, (loaded) => {
    if (loaded && !isLoggedIn.value && isVisible.value) {
        hideBanner();
    }
});

const openInfoModal = () => {
    if (bannerData.value) {
        new ServerMessaging().loadAndDisplayDialog({
            title: bannerData.value.title,
            content: bannerData.value.message,
            isFullscreen: false,
            buttons: [{
                label: 'Close',
                action: 'hide'
            }]
        }, true);
    }
};

onMounted(() => {
    const cached = cache.getPersistent(BANNER_CACHE_KEY);
    if (cached) {
        showBanner(cached);
    }
    pubsub.subscribe(C_EVENTS.BANNER_MESSAGE_EVENT, showBanner);
});

onBeforeUnmount(() => {
    pubsub.unsubscribe(C_EVENTS.BANNER_MESSAGE_EVENT, showBanner);
});
</script>

<style scoped lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.utd-message-banner {
  .ds1-pa-1();
  border-radius: 50px;
  background-color: @WKCE-BLUE-TINT3;
  margin-top: 60px;
  margin-left: auto;
  margin-right: auto;
  max-width: calc(100vw - 32px);
  z-index: 6;
  left: 50%;
  transform: translate(-50%, 0);
  height: 24px;
  overflow: hidden;
}

.utd-message-banner-text {
  .ds1-ma-0();
  .ds1-ph-3();
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.utd-message-banner-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.utd-message-banner-info-btn {
  .ds1-pa-0();
  .ds1-ml-1();
  margin-top: 5px;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

@media only screen and (min-width: 768px) {
  .utd-message-banner {
    .ds1-mh-0();
    margin-top: 52px;
    border-radius: 0;
    width: 100vw;
    box-sizing: content-box;

    .utdRoute-tableOfContentsSection &,
    .utdRoute-tableOfContentsSpecialty &,
    :not(.isHomeSearchEmpty) & {
      margin-top: 52px;
    }
  }

  .utd-message-banner-text {
    justify-content: flex-start;
  }
}

</style>
